import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { PRIMARY_LOCALES, type Locale } from "@laftel-ip-originals/notion/locale";
import type { Translation } from "~/types/i18n";
import { ko } from "./ko";
import { en } from "./en";
import { ja } from "./ja";
import { zhCn } from "./zh-cn";
import { zhTw } from "./zh-tw";

const translations: Record<Locale, Translation> = {
  ko,
  en,
  ja,
  "zh-cn": zhCn,
  "zh-tw": zhTw,
};

const visibleLocales = PRIMARY_LOCALES;

interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: Translation;
}

export const I18nContext = createContext<I18nContextValue | null>(null);

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useI18n must be used within I18nProvider");
  }
  return ctx;
}

function detectLocale(): Locale {
  const stored = localStorage.getItem("locale") as Locale | null;
  if (stored && visibleLocales.includes(stored as (typeof visibleLocales)[number])) {
    return stored;
  }

  return "ko";
}

export function useI18nProvider() {
  const [locale, setLocaleState] = useState<Locale>(detectLocale);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.body.classList.remove("lang-en", "lang-kr");
    if (locale === "en") document.body.classList.add("lang-en");
    if (locale === "ko") document.body.classList.add("lang-kr");
  }, [locale]);

  const setLocale = useCallback((newLocale: Locale) => {
    setLocaleState(newLocale);
    localStorage.setItem("locale", newLocale);
  }, []);

  return {
    locale,
    setLocale,
    t: translations[locale],
  };
}

export const LOCALES: { value: Locale; label: string }[] = [
  { value: "ko", label: "KR" },
  { value: "en", label: "EN" },
  { value: "ja", label: "JP" },
];
